import type { QueryColumn } from '@/api/contracts/queryResult';
import type {
  DataViewColumn,
  DataViewColumnDisplayItem,
  DataViewColumnDisplayPreferenceEntry
} from '@/api/contracts/dataModel';

/**
 * Bygger QueryColumn fra kjoere-responsen + column-display-metadata.
 *
 * US 2884: kjoere-responsen ER den autoritative grid-kolonnen. Backend har allerede loest opp
 * rekkefoelge, ledetekst, bredde, synlighet og nullbarhet - inkludert brukerens lagrede
 * kolonneoppsett - saa denne funksjonen kopierer verdiene urort. Ingen default-bredde-formel, ingen
 * tolkning av null-haandteringskoden, ingen egen flette-loekke. Display-metadataen
 * (primaryFieldType, componentEdit, relasjonsoppslag) er en annen kilde og flettes fortsatt inn;
 * brukerens eget tallformat kommer paa kjoere-kolonnen og legges paa etterpaa saa det vinner over
 * tabelldefinisjonens format.
 */

export function buildQueryColumnsFromDataViewRun (
  runColumns: DataViewColumn[],
  displayItems: DataViewColumnDisplayItem[],
  mainTableKey: string | null,
  mainAlias: string | null
): QueryColumn[] {
  const columns: QueryColumn[] = runColumns.map((col, idx) => ({
    field: col.columnId,
    displayOrdinal: idx + 1,
    label: col.label,
    width: col.width,
    showInList: col.showInList,
    showInDetail: col.showInDetail,
    includeInExport: col.includeInExport,
    dataType: col.dataType ?? null,
    isReadOnly: col.isReadOnly ?? true,
    tableModelId: col.tableModelId ?? null,
    isNullable: col.isNullable,
    nullHandling: col.nullHandling,
    columnKey: col.columnKey ?? null,
    sourceColumnName: col.sourceColumnName ?? null,
    isSystemColumn: col.isSystemColumn === true
  }));
  mergeDataViewColumnDisplay(columns, displayItems, mainTableKey, mainAlias);
  for (let i = 0; i < columns.length; i++) {
    const runColumn = runColumns[i];
    if (runColumn.numberFormatShowThousandsSeparator != null) {
      columns[i].numberFormatShowThousandsSeparator = runColumn.numberFormatShowThousandsSeparator;
    }
    if (runColumn.numberFormatDecimalPrecision != null) {
      columns[i].numberFormatDecimalPrecision = runColumn.numberFormatDecimalPrecision;
    }
  }
  return columns;
}

export function mergeDataViewColumnDisplay (
  columns: QueryColumn[],
  items: DataViewColumnDisplayItem[],
  mainTableKey: string | null,
  mainAlias: string | null
): void {
  const map = new Map(items.map((i) => [i.columnId, i]));
  for (const col of columns) {
    const d = map.get(col.field);
    if (!d) continue;
    applyDisplayItemToColumn(col, d);
    assignMainTableDefinition(col, mainTableKey, mainAlias);
  }
}

/** Copies every set (non-null) display metadata field from the item onto the column. */
function applyDisplayItemToColumn (col: QueryColumn, d: DataViewColumnDisplayItem): void {
  if (d.primaryFieldType != null) col.primaryFieldType = d.primaryFieldType;
  if (d.componentEdit != null) col.componentEdit = d.componentEdit;
  if (d.relationLookupEnabled != null) col.relationLookupEnabled = d.relationLookupEnabled;
  if (d.relationTargetTableKey != null) col.relationTargetTableKey = d.relationTargetTableKey;
  if (d.numberFormatShowThousandsSeparator != null) {
    col.numberFormatShowThousandsSeparator = d.numberFormatShowThousandsSeparator;
  }
  if (d.numberFormatDecimalPrecision != null) {
    col.numberFormatDecimalPrecision = d.numberFormatDecimalPrecision;
  }
  if (d.columnKey != null) col.columnKey = d.columnKey;
}

/** Sets tableDefinitionColumn only for columns that belong to the main table. */
function assignMainTableDefinition (
  col: QueryColumn,
  mainTableKey: string | null,
  mainAlias: string | null
): void {
  const belongsToMainTable =
    (col.tableModelId ?? '').trim().length > 0
    && (mainAlias ?? '').trim().length > 0
    && String(col.tableModelId).trim().toLowerCase() === String(mainAlias).trim().toLowerCase();
  if (mainTableKey && col.columnKey && belongsToMainTable) {
    col.tableDefinitionColumn = { tableKey: mainTableKey };
  }
}

/**
 * WI #1279: kontraktsbrudd-deteksjon for DataView-kolonner.
 *
 * En kolonne er save-bar HVIS OG BARE HVIS backend har levert hele kontrakten:
 * isReadOnly=false + tableDefinitionColumn.tableKey + columnKey. Ingen fallback
 * til andre felter (`field`/`columnName`/`sourceColumnName`) — det var rotårsaken
 * til at Vaffeldag-Quantity sendte SQL-aliasen `ordre_Quantity` og fikk 400.
 *
 * Se `feedback_fe_consumes_be_contracts_verbatim` — FE skal ALDRI mene noe om
 * strukturer. Hvis kontrakten mangler, er kolonnen ikke save-bar, og brukeren
 * får en eksplisitt feilmelding via `findDataViewContractViolations`.
 */
export function isDataViewColumnSaveable (column: QueryColumn): boolean {
  return column.isReadOnly === false
    && column.tableDefinitionColumn?.tableKey != null
    && column.columnKey != null;
}

/** Form-bærende kolonne for "ny rad": en skrivbar bruker-kolonne (ikke read-only/system/computed). */
export interface NewRowColumnLike {
  isReadOnly?: boolean | null;
  isSystemColumn?: boolean | null;
  isComputed?: boolean | null;
  includeInputFormNew?: boolean | null;
}

/**
 * Bug 2122/2322: én felles definisjon på "ny-rad-kolonne" — brukes av både "Legg til rad"-knappen
 * (aktivering) og ny-rad-skjemaet (feltbygging), slik at de aldri kommer i utakt (aktiv knapp ⇒ skjema
 * med minst ett felt).
 *
 * Bug 2322: gaten honorerer den lagrede kurerings-verdien `includeInputFormNew` (satt som default ved
 * oppretting per nullhandling — påkrevd→PÅ, standardverdi→AV, nullbar→PÅ — aktivt styrt i oppsett, og
 * migrert for eksisterende tabeller). Systemkolonner og beregnede kolonner er aldri med (kan ikke
 * fylles). Flagget overstyrer read-only, slik at et read-only-men-påkrevd-ved-oppretting-felt vises som
 * input i «Ny rad».
 */
export function isAddableNewRowColumn (column: NewRowColumnLike): boolean {
  return column.isSystemColumn !== true && column.isComputed !== true && column.includeInputFormNew === true;
}

/** Sant når tabellen har minst én redigerbar ny-rad-kolonne (jf. {@link isAddableNewRowColumn}). */
export function tableHasAddableNewRowColumns (
  table: { columns?: ReadonlyArray<NewRowColumnLike> | null } | null | undefined
): boolean {
  return table?.columns?.some(isAddableNewRowColumn) ?? false;
}

export interface DataViewContractViolation {
  columnId: string;
  label: string;
  reason: 'writable_without_columnKey';
}

/**
 * WI #1279: returnerer alle kolonner hvor backend har et selvmotstridende
 * kontrakt — flagget skrivbar (isReadOnly=false) uten å levere columnKey
 * som save-key. Hvis listen er ikke-tom, skal caller vise eksplisitt
 * feilmelding (notificationsStore.addError) — aldri stille degradering.
 */
export function findDataViewContractViolations (columns: QueryColumn[]): DataViewContractViolation[] {
  const out: DataViewContractViolation[] = [];
  for (const col of columns) {
    if (col.isReadOnly === false && col.columnKey == null) {
      out.push({
        columnId: col.field,
        label: col.label,
        reason: 'writable_without_columnKey'
      });
    }
  }
  return out;
}

/**
 * US 3399: hver edit-komponent og hvorvidt den tar imot en `id` som havner paa en ekte kontroll.
 *
 * Fakta hoerer her, ikke i en liste hos den som rendrer. En kaller som vil knytte en etikett med
 * `for=` maa vite om det finnes noe aa peke paa, og svaret er en egenskap ved komponenten - kjent
 * naar den skrives, ikke noe som skal utledes av DOM-en etterpaa.
 *
 * `false` betyr at komponenten ikke rendrer en labelable kontroll i det hele tatt:
 * - editList rendrer bare chips, editReadOnly bare tekst, editComment en visning med dialog.
 * - editBool bruker BaseOnOff, som ALLEREDE lager sin egen stabile id og sin egen <label for=>
 *   (se kommentaren der, S6853). En ekstern id ville kollidert med en kobling som virker.
 *
 * ⛔ Aa sette en id paa wrapper-diven i disse ville gitt statisk analyse et gyldig for/id-par mens
 * HTMLLabelElement.control fortsatt var null - altsaa en blindsone laget med vilje for aa slukke en
 * regel. Derfor er `false` her et svar, ikke en mangel.
 *
 * Returtypen til resolveren er noeklene her, saa en ny edit-komponent MAA ta stilling til dette for
 * aa kunne resolves. Det finnes ingen liste andre steder som kan drifte fra denne.
 */
const EDIT_COMPONENT_ACCEPTS_ID = {
  editString: true,
  editNumber: true,
  editDate: true,
  editPercent: true,
  editTime: true,
  editAutoComplete: true,
  editBool: false,
  editList: false,
  editComment: false,
  editReadOnly: false
} as const;

export type EditComponentName = keyof typeof EDIT_COMPONENT_ACCEPTS_ID;

/**
 * US 3399: kan en etikett knyttes til denne komponenten med `for=`/`id`?
 *
 * Er svaret nei, skal kalleren IKKE sette `for=` - en peker til en id som ikke finnes gjoer
 * `label.control` til null og oedelegger koblingen omslutning ellers gir.
 */
export function editComponentAcceptsId (name: string): boolean {
  return EDIT_COMPONENT_ACCEPTS_ID[name as EditComponentName] === true;
}

/**
 * Bugfix 3526: true when the server fills this column in on save, so the user neither can nor must
 * supply a value. The backend declares it by giving the column the read-only edit component, which
 * renders no input at all, and the insert path then overrides whatever value a client sends for it.
 * A form must therefore never mark such a column as required, never offer a per-field copy action
 * for it, and never carry its value over from the row being copied.
 *
 * Takes the edit-component name rather than a whole column because the two callers hold it under the
 * same name from slightly different sources - the new-row form stores the resolved component, the
 * copy dialog stores the raw componentEdit from the contract. Both read editReadOnly for this case.
 */
export function isServerAssignedColumn (columnType: string | null | undefined): boolean {
  return (columnType ?? '').toLowerCase() === 'editreadonly';
}

export function resolveQueryColumnEditComponent (column: QueryColumn): EditComponentName {
  if (column.relationLookupEnabled === true) return 'editAutoComplete';
  const ce = (column.componentEdit ?? '').toLowerCase();
  // WI 2205: server-tildelte felt (f.eks. Løpenummer) rendres skrivebeskyttet/auto.
  if (ce === 'editreadonly') return 'editReadOnly';
  if (ce === 'editcomment') return 'editComment';
  if (ce === 'editautocomplete') return 'editAutoComplete';
  if (ce === 'editpercent') return 'editPercent';
  if (ce === 'editbool') return 'editBool';
  if (ce === 'editdate') return 'editDate';
  if (ce === 'edittime') return 'editTime';
  if (ce === 'editnumber') return 'editNumber';
  if (ce === 'editlist') return 'editList';
  return 'editString';
}

export interface MassUpdateColumn {
  id: string;
  title: string;
  field: string;
  columnKeyForMassUpdate: string | null | undefined;
  columnType: string;
  componentEdit: string | null | undefined;
  primaryFieldType: string | null | undefined;
  isCommentColumn: boolean;
  commentContractError: string | null;
  isReadOnly: boolean;
  /** Editor-header config – sendt videre som :header til <component :is="columnType">. */
  relationLookupEnabled: boolean;
  dataViewId: string;
  dataViewColumnId: string;
  isNullable: boolean | undefined;
  nullHandling: 'allow' | 'required' | 'default' | undefined;
}

export function buildMassUpdateColumn (
  column: QueryColumn,
  dataViewId: string
): MassUpdateColumn {
  const commentContract = getCommentColumnContractResult(column);
  return {
    id: column.field,
    title: column.label,
    field: column.field,
    columnKeyForMassUpdate: column.columnKey,
    columnType: resolveQueryColumnEditComponent(column),
    componentEdit: column.componentEdit,
    primaryFieldType: column.primaryFieldType,
    isCommentColumn: commentContract.isCommentColumn,
    commentContractError: commentContract.error,
    isReadOnly: column.isReadOnly,
    relationLookupEnabled: column.relationLookupEnabled === true,
    dataViewId,
    dataViewColumnId: column.field,
    isNullable: column.isNullable,
    nullHandling: column.nullHandling
  };
}

export interface CommentColumnContractResult {
  isCommentColumn: boolean;
  error: string | null;
}

export function getCommentColumnContractResult (column: QueryColumn): CommentColumnContractResult {
  const componentEdit = (column.componentEdit ?? '').trim().toLowerCase();
  const primaryFieldType = (column.primaryFieldType ?? '').trim().toLowerCase();
  const hasColumnKey = typeof column.columnKey === 'string' && column.columnKey.trim().length > 0;

  const markedCommentByEdit = componentEdit === 'editcomment';
  const markedCommentByType = primaryFieldType === 'comment';
  const isCommentColumn = markedCommentByEdit || markedCommentByType;

  if (!isCommentColumn) {
    return { isCommentColumn: false, error: null };
  }

  if (!markedCommentByEdit) {
    return {
      isCommentColumn: true,
      error: 'Kolonnen mangler componentEdit=editComment fra backend og kan ikke masseoppdateres.'
    };
  }

  if (!markedCommentByType) {
    return {
      isCommentColumn: true,
      error: 'Kolonnen mangler primaryFieldType=Comment fra backend og kan ikke masseoppdateres.'
    };
  }

  if (!hasColumnKey) {
    return {
      isCommentColumn: true,
      error: 'Kolonnen mangler columnKey fra backend og kan ikke masseoppdateres.'
    };
  }

  return { isCommentColumn: true, error: null };
}
